import type { Phone } from '../types'
import { MetricBadge, type MetricSeverity } from './MetricBadge'
import { BatteryGauge } from './BatteryGauge'
import { SimStatusIcon } from './SimStatusIcon'
import {
  DASH,
  WEAK_SIGNAL_BARS,
  formatRelativeTime,
  formatSignal,
  formatUptime,
} from '../lib/metricFormat'

/**
 * Signal severity: ≤1 bar = yellow (weak), 0 = same yellow (no service
 * is already covered by SIM/network badges separately).
 */
function signalSeverity(phone: Phone): MetricSeverity {
  const bars = phone.metrics?.signalBars ?? null
  if (bars === null) return 'neutral'
  if (bars <= WEAK_SIGNAL_BARS) return 'warn'
  return 'ok'
}

/** SIM cell — status glyph; colour + overlay encode the state. */
export function SimCell({ phone }: { phone: Phone }) {
  return <SimStatusIcon state={phone.metrics?.simState ?? null} />
}

/** Signal cell — network type + bars in one badge. */
export function SignalCell({ phone }: { phone: Phone }) {
  const m = phone.metrics
  return (
    <MetricBadge
      label={formatSignal({
        networkType: m?.networkType ?? null,
        signalBars: m?.signalBars ?? null,
      })}
      severity={signalSeverity(phone)}
      title={
        m?.signalDbm !== null && m?.signalDbm !== undefined
          ? `Сигнал ${m.signalDbm} дБм`
          : 'Сигнал'
      }
    />
  )
}

/**
 * Uptime + freshness — two-line cell. Top is phone uptime since last
 * boot ("5д 3ч"); bottom is age of the last telemetry snapshot
 * ("12с назад") so admins can spot a stale "online" dot.
 */
export function UptimeCell({ phone }: { phone: Phone }) {
  const m = phone.metrics
  return (
    <div className="leading-tight">
      <div
        className="text-xs text-gray-700 dark:text-gray-300"
        title="Аптайм с последнего ребута"
      >
        {formatUptime(m?.uptimeSeconds ?? null)}
      </div>
      <div
        className="text-[10px] text-gray-500 dark:text-gray-400"
        title={
          m?.metricsUpdatedAt
            ? `Последняя телеметрия: ${m.metricsUpdatedAt}`
            : 'Телеметрия ещё не приходила'
        }
      >
        {m?.metricsUpdatedAt ? formatRelativeTime(m.metricsUpdatedAt) : DASH}
      </div>
    </div>
  )
}

/** Battery cell — CSS gauge with charge level + charging bolt. */
export function BatteryCell({ phone }: { phone: Phone }) {
  const m = phone.metrics
  return (
    <BatteryGauge
      pct={m?.batteryPct ?? null}
      px={13}
      charging={m?.batteryCharging ?? false}
      title={m?.batteryPlugged ? `Зарядка: ${m.batteryPlugged}` : 'Заряд батареи'}
    />
  )
}
